import {createContext, memo, type MutableRefObject, useCallback, useMemo, useRef, useState} from 'react'

import type {CreateIterationConfiguration} from '../../api/columns/contracts/api'
import type {MemexColumn, MemexColumnDataType, MemexProjectColumnId} from '../../api/columns/contracts/memex-column'
import type {NewOptions} from '../../api/columns/contracts/single-select'
import type {CustomColumnKind} from '../../api/columns/contracts/storage'
import type {MemexItem} from '../../api/memex-items/contracts'
import type {PaginatedMemexItemsData} from '../../api/memex-items/paginated-views'
import {fetchJSONIslandData} from '../../helpers/json-island'
import {useLazyRef} from '../../hooks/common/use-lazy-ref'
import {type ColumnModel, createColumnModel} from '../../models/column-model'
import {useIssueFieldsEnabled} from '../issue-fields/use-issue-fields-enabled'
import {buildInitialItemsAndColumns, getFlatItems} from '../memex-items/memex-items-data'

/**
 * Currently only single select and iteration columns are allowed to send their settings hash.
 *
 * If you find yourself in a situation where a column type now needs to send
 * settings, update this definition and ensure the backend works the same.
 */
type ColumnOptions =
  | {
      type: typeof MemexColumnDataType.SingleSelect
      settings: {options?: NewOptions}
    }
  | {
      type: typeof MemexColumnDataType.Iteration
      settings: {configuration: CreateIterationConfiguration}
    }
  | {
      type: Exclude<CustomColumnKind, typeof MemexColumnDataType.SingleSelect | typeof MemexColumnDataType.Iteration>
      settings: undefined
    }

export type AddColumnRequest = {
  name: string
} & ColumnOptions

export type ColumnsContextType = {
  /** A list of field names defined on the server that are not allowed to be used */
  reservedColumnNames: Array<string>

  /**
   * Updates the state tracking the list of columns for the project.
   * @param newColumnModels The full set of new columns
   */
  setAllColumns: (newColumnModels: Array<ColumnModel>) => void

  /** A list of columns for the project */
  allColumns: Array<ColumnModel>

  /** A ref to the list of all columns for the project. */
  allColumnsRef: Readonly<MutableRefObject<Readonly<Array<ColumnModel>>>>
}

export type ColumnsStableContextType = Pick<ColumnsContextType, 'setAllColumns' | 'allColumnsRef'>

/**
 * This context manages state related to the columns of a project. Because this particular
 * context contains the state object itself, whenever the state changes, components that
 * consume this context (either directly with a `useContext` or indirectly with a hook) that
 * consumes this context), will be re-rendered.
 *
 * If a component should not be re-rendered when the state changes, consider consuming the `ColumnsStableContext`
 * instead.
 */
export const ColumnsContext = createContext<ColumnsContextType | null>(null)

/**
 * This context allows consumers to access and mutate the columns of a project. Because this particular
 * context only contains a reference to the state, as well as updater functions to modify the state,
 * the context value will be stable (i.e. the same object) for the lifetime of the context. A component
 * (or hook) which consumes this context will _not_ be re-rendered when the state changes.
 *
 * If a component should be re-rendered when the state changes, consider consuming the
 * `ColumnsContext` instead.
 */
export const ColumnsStableContext = createContext<ColumnsStableContextType | null>(null)

export const ColumnsStateProvider = memo<{children: React.ReactNode}>(function ColumnsStateProvider({children}) {
  const issueFieldsEnabled = useIssueFieldsEnabled()
  const reservedColumnNamesRef = useLazyRef(() => fetchJSONIslandData('reserved-column-names') || [])
  const [allColumns, setAllColumnsState] = useState(() => {
    let initialColumns = fetchJSONIslandData('memex-columns-data') || []

    if (!issueFieldsEnabled) {
      initialColumns = initialColumns.filter(column => !column.issueFieldId)
    }

    return buildColumnModels(initialColumns)
  })
  const allColumnsRef = useRef<Readonly<Array<ColumnModel>>>(allColumns)

  const setAllColumns = useCallback((newColumnModels: Array<ColumnModel>) => {
    setAllColumnsState(newColumnModels)
    allColumnsRef.current = newColumnModels
  }, [])

  const contextValue: ColumnsContextType = useMemo(() => {
    return {
      // eslint-disable-next-line react-hooks/refs
      reservedColumnNames: reservedColumnNamesRef.current,
      setAllColumns,
      allColumns,
      allColumnsRef,
    }
  }, [reservedColumnNamesRef, setAllColumns, allColumns])

  const stableContextValue: ColumnsStableContextType = useMemo(() => {
    return {
      setAllColumns,
      allColumnsRef,
    }
  }, [allColumnsRef, setAllColumns])

  return (
    // eslint-disable-next-line react-hooks/refs
    <ColumnsContext value={contextValue}>
      <ColumnsStableContext value={stableContextValue}>{children}</ColumnsStableContext>
    </ColumnsContext>
  )
})

/**
 * Returns a new array with ColumnModels to match the array of incoming MemexColumns
 * Will create ColumnModels out of the MemexColumn objects if they do not exist, and call updateModel
 * on any ColumnModels that do still exist to update their observable fields.
 *
 * @param incomingColumns List of MemexColumn objects which should be the new state
 * @returns A list of ColumnModel objects which should be used for the new state by the ColumnsStateProvider
 */
export function buildColumnModels(incomingColumns: Array<MemexColumn>) {
  return incomingColumns.map(incomingColumn => createColumnModel(incomingColumn))
}

export function buildColumnIdsForItemsData(memexItems: Array<MemexItem> | PaginatedMemexItemsData) {
  const items = getFlatItems(memexItems)
  const loadedColumnIds = new Set<MemexProjectColumnId>()
  if (items?.length) {
    const itemWithMaxColumns = items.sort(
      (a, b) => b.memexProjectColumnValues.length - a.memexProjectColumnValues.length,
    )[0]
    if (itemWithMaxColumns) {
      for (const columnValue of itemWithMaxColumns.memexProjectColumnValues) {
        loadedColumnIds.add(columnValue.memexProjectColumnId)
      }
    }
  }
  return loadedColumnIds
}

export function buildInitialLoadedColumnIds() {
  const {memexItems} = buildInitialItemsAndColumns('memex-paginated-items-data')
  return buildColumnIdsForItemsData(memexItems)
}
